import {
  Box,
  Flex,
  Grid,
  Link,
  spacing,
  SpotifySvg,
  StyleProps,
  useIsMobileView,
} from 'deepstash-ui';
import React, { useMemo } from 'react';
import { ArticleStatus, SourceSubtype } from 'types/enums';
import { HeadingSize } from 'types/globalTypes';
import { Source } from 'types/models';
import { InfoLinkWrapper } from '../InfoLinkWrapper';
import { SourceTitleByTypeSize } from '../SourceTitleByType';
import BaseSourceTitle from '../BaseSourceTitle';
import SourceDomain from '../SourceDomain';
import SourceImageV2 from '../source-image/SourceImageV2';
import {
  SOURCE_COLLAPSED_LARGE_IMAGE_SIZE_MOBILE,
  SOURCE_COLLAPSED_LARGE_IMAGE_SIZE_DESKTOP,
} from 'utils/constants';

interface SourcePodcastTitleProps {
  source: Source;
  titleHeadingSize: 'p' | HeadingSize;
  /**
   * Whether or not there is a link over the resource info
   */
  withLink?: boolean;
  /**
   * @default true
   */
  showPlayer?: boolean;
  size?: SourceTitleByTypeSize;
  /**
   * Wether to have a static sourceTitle that is not interactable
   */
  isNotInteractable?: boolean;
  preload?: boolean;
}

const SourcePodcastTitle: React.FC<SourcePodcastTitleProps & StyleProps> = ({
  source,
  noOfLines,
  titleHeadingSize,
  withLink,
  showPlayer = true,
  size,
  isNotInteractable,
  preload,
  ...props
}) => {
  const isMobileView = useIsMobileView();

  const subTitle = useMemo(
    () =>
      isNotInteractable ? (
        <Flex
          alignItems="center"
          justifyContent="flex-start"
          width="100%"
          zIndex={1}
        >
          {source.sourceSubtype === SourceSubtype.PODCAST_SPOTIFY && (
            <SpotifySvg
              boxSize={size === 'sm' ? spacing.S.rem : spacing.L.rem}
              color="#1DB954"
              mr={spacing.XXS.rem}
              zIndex={1}
            />
          )}
          <SourceDomain
            domain={source?.author ?? ''}
            size={size}
            isNotInteractable={isNotInteractable}
          />
        </Flex>
      ) : (
        <Link
          href={source?.url ?? ''}
          _focus={undefined}
          _hover={{ textDecoration: 'underline' }}
          _active={undefined}
          isExternal
          onClick={e => e.stopPropagation()}
          // For security reasons, we must add these:
          rel="noopener nofollow noreferrer"
        >
          <Flex
            alignItems="center"
            justifyContent="flex-start"
            width="100%"
            zIndex={1}
          >
            {source.sourceSubtype === SourceSubtype.PODCAST_SPOTIFY && (
              <SpotifySvg
                boxSize={size === 'sm' ? spacing.S.rem : spacing.L.rem}
                color="#1DB954"
                mr={spacing.XXS.rem}
                zIndex={1}
              />
            )}
            <SourceDomain domain={source?.author ?? ''} size={size} />
          </Flex>
        </Link>
      ),
    [size, source, isNotInteractable],
  );

  const leftItem = useMemo(
    () => (
      <SourceImageV2
        source={source}
        size={
          isMobileView
            ? SOURCE_COLLAPSED_LARGE_IMAGE_SIZE_MOBILE
            : SOURCE_COLLAPSED_LARGE_IMAGE_SIZE_DESKTOP
        }
        isNotInteractable={isNotInteractable}
        preload={preload}
      />
    ),
    [size, source, isNotInteractable, isMobileView],
  );

  const showPlayerCondition =
    showPlayer &&
    source.status === ArticleStatus.DRAFT &&
    source.sourceSubtype === SourceSubtype.PODCAST_SPOTIFY;

  return (
    <Grid alignItems="center" {...props} w="90%" justifyContent="flex-start">
      <InfoLinkWrapper
        source={source}
        withLink={withLink}
        isNotInteractable={isNotInteractable}
      >
        <BaseSourceTitle
          mainTitle={source.title}
          leftItem={leftItem}
          subTitle={subTitle}
          size={size}
          source={source}
          titleHeadingSize={titleHeadingSize}
          noOfLines={noOfLines}
          {...props}
        />
      </InfoLinkWrapper>

      {/* Embed the player */}
      {showPlayerCondition && (
        <Box
          width="100%"
          mt={spacing.M.rem}
          pr={spacing.S.rem}
          height={spacing.toRem(240)}
        >
          <iframe
            // Get the embeded version
            src={source.url?.replace(
              'spotify.com',
              'spotify.com/embed-podcast',
            )}
            width="100%"
            height="100%"
            allowTransparency={true}
            allow="encrypted-media"
          />
        </Box>
      )}
    </Grid>
  );
};
export default SourcePodcastTitle;
